import * as Yup from 'yup'
import dayjs from 'dayjs'

const CARD_BRAND_REGEXP =
  /^(?:4\d{12}(?:\d{3})?|5[1-5]\d{14}|2(?:2[2-9]\d|[3-6]\d{2}|7[01]\d|720)\d{12}|3[47]\d{13}|6(?:011|5\d{2}|4[4-9]\d)\d{12}|(?:2131|1800|35\d{3})\d{11})$/

const isValidLuhn = (num: string): boolean => {
  let sum = 0
  let doubleIt = false

  for (let i = num.length - 1; i >= 0; i--) {
    let digit = num.charCodeAt(i) - 48

    if (digit < 0 || digit > 9) {
      return false
    }

    if (doubleIt) {
      digit *= 2

      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    doubleIt = !doubleIt
  }

  return sum % 10 === 0
}

const isValidCreditCardNumber = (value: string): boolean => {
  const cleanNumber = value.replace(/\s+/g, '')

  if (!/^\d{13,19}$/.test(cleanNumber)) {
    return false
  }

  if (!CARD_BRAND_REGEXP.test(cleanNumber)) {
    return false
  }

  return isValidLuhn(cleanNumber)
}

const isValidRoutingNumber = (routingNumber: string): boolean => {
  if (!/^\d{9}$/.test(routingNumber)) {
    return false
  }

  const digits = routingNumber.split('').map(Number)
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    1 * (digits[2] + digits[5] + digits[8])

  return checksum % 10 === 0
}

const isValidAccountNumber = (accountNumber: string): boolean => {
  return /^\d{6,17}$/.test(accountNumber)
}

Yup.addMethod(Yup.string, 'validateCreditCardNumber', function (errorMessage) {
  return this.test('test-credit-card-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidCreditCardNumber(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateRoutingNumber', function (errorMessage) {
  return this.test('test-routing-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidRoutingNumber(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateAccountNumber', function (errorMessage) {
  return this.test('test-account-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidAccountNumber(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateExpirationDate', function (errorMessage) {
  return this.test(`test-expiration-date`, errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return createError({ path, message: errorMessage })
    }

    const cleanValue = value.replace(/undefined/g, '').replace(/--/g, '-')

    const date = dayjs(cleanValue, 'MM-YYYY', true)

    if (!date.isValid()) {
      return createError({ path, message: 'Date must be in MM-YYYY format' })
    }

    if (date.isBefore(dayjs(), 'month')) {
      return createError({ path, message: 'Expiration date cannot be in the past' })
    }

    return true
  })
})

export const DebitCardSchema = Yup.object().shape({
  nameOnCard: Yup.string().required('Name on card is required'),
  cardNumber: Yup.string()
    .required('Card number is required')
    // @ts-ignore
    .validateCreditCardNumber('Invalid card number'),
  expirationDate: Yup.string()
    .required('Expiration date is required')
    // @ts-ignore
    .validateExpirationDate('Expiration date is invalid'),
  zipCode: Yup.string().required('Zip code is required'),
})

export const BankInformationSchema = Yup.object().shape({
  routingNumber: Yup.string()
    .required('Routing number is required')
    // @ts-ignore
    .validateRoutingNumber('Invalid routing number'),
  accountNumber: Yup.string()
    .required('Account number is required')
    // @ts-ignore
    .validateAccountNumber('Account number must be 6-17 digits'),
  accountType: Yup.string().required('Account type is required'),
})
